import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";

import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";
import ArchivedElementBanner from "./ArchivedElementBanner";
import { usePublicFile } from "./useFormResultUpload";
import { useImageLightbox } from "./ImageLightbox";

/** Minimal reference stored at design time — just the attachment id. */
interface FileRefValue {
  fileId: number;
}

interface Props {
  element: DocumentElementType;
}

/**
 * Display-only element rendering an image the designer attached at
 * design time. Element data carries only the fileId; the signed URL is
 * resolved at render via the host's `fetchPublicFileUrl`.
 */
const DisplayImage: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    caption?: string;
    altText?: string;
    image?: FileRefValue;
    hidden?: boolean;
  };
  const fileId = data?.image?.fileId;
  const caption = data?.caption;
  const { file, loading } = usePublicFile(fileId);
  const { openImage, lightbox } = useImageLightbox();
  const altText = data?.altText || caption || file?.name || "Image";

  if (fileId == null) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/25 p-6 text-sm text-muted-foreground">
          <FontAwesomeIcon icon={faImage} className="h-4 w-4" />
          No image attached
        </div>
        <ArchivedElementBanner archived={element.archived} />
      </div>
    );
  }

  return (
    <figure className="space-y-2">
      {file?.url ? (
        <button
          type="button"
          onClick={() =>
            openImage({
              fileId: fileId!,
              name: altText,
              previewUrl: file.thumbnailUrl ?? file.url,
              fullUrl: file.url,
              design: true,
            })
          }
          className="block cursor-zoom-in"
          aria-label={`View ${altText}`}
        >
          <img
            src={file.thumbnailUrl ?? file.url}
            alt={altText}
            className="max-w-full rounded-md"
            loading="lazy"
          />
        </button>
      ) : (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/25 p-6 text-sm text-muted-foreground">
          <FontAwesomeIcon icon={faImage} className="h-4 w-4" />
          {loading ? "Loading image…" : "Image unavailable"}
        </div>
      )}
      {caption && (
        <figcaption className="text-xs text-muted-foreground">
          {caption}
        </figcaption>
      )}
      {lightbox}
      <ArchivedElementBanner archived={element.archived} />
    </figure>
  );
};

export default DisplayImage;
